import { useEffect, useState } from "react";
import "./ReaderPage.css";
import { Page as PdfPage } from "react-pdf";
import { Document } from "react-pdf/dist/esm/entry.webpack";
import UploadService from "../../../services/upload/UploadService";
import Page from "../../../interfaces/Page";
import StandardModal from "../../Modals/StandardModal";
import ModalSize from "../../Modals/ModalSize";
import useModal from "../../Modals/UseModal";
import Speech from "react-speech";
import { connect } from "react-redux";
import { RootState } from "../../../state/configureStore";
import Loading from "../../Loading/Loading";
import OwlBotWord from "./OwlBotWord";
import { ReadleeSettingsState } from "../../../state/readlee/readleeReducer";

type ReaderPageProps = ReadleeSettingsState & {
  page?: Page;
  fontSize: number;
  pageNumber: number;
  bookName: string;
  viewType?: undefined | "pdf" | "text" | "external" | "paperback";
  pdfPath?: string;
  correctString?: string;
  dirtyDefinitions: Array<string>;
};

interface OwlBot {
  definitions: Array<OwlBotDefinition>;
  word: string;
  pronunciation?: string;
}

interface OwlBotDefinition {
  type?: string;
  definition: string;
  example?: string;
  image_url?: string;
  emoji?: string;
}

const enableTranscriptView = false;

const ReaderView = (props: ReaderPageProps) => {
  const [pdfPath, setPdfPath] = useState("");
  const [word, setWord] = useState("");
  const [def, setDef] = useState("");
  const { open, openModal, closeModal } = useModal(false);

  useEffect(() => {
    if (props.viewType === "pdf" && props.pdfPath) {
      UploadService.getPdfPath(props.pdfPath).then(path => {
        setPdfPath(path);
      });
    }
  }, [props.pdfPath, props.viewType]);

  const owlBotOnClick = (word: string) => {
    word = word.split("\n")?.[0]?.replace(/\W/g, "").toLowerCase() || "";
    const headers = { Authorization: "Token 9923d6e1403e9316555a6842eabc3e03a0bff34b" };
    fetch("https://owlbot.info/api/v4/dictionary/" + word, { headers }).then(res => {
      res
        .json()
        .then((v: OwlBot) => {
          let def = "No definition available";
          if (v.definitions) {
            def = "\n\n";
            v.definitions.forEach(element => {
              if (element.type === null) {
                def = "No definition available";
              } else if (!props.dirtyDefinitions.includes(element.definition)) {
                def += element.type + ": " + element.definition + "\n\n";
              }
            });
          }
          setWord(word);
          setDef(def);
          openModal();
        })
        .catch(() => {
          setWord(word);
          setDef("No definition available");
          openModal();
        });
    });
  };

  let results;

  if (props.viewType === "pdf") {
    if (pdfPath) {
      results = (
        <div className="pdfContentDiv">
          <Document loading={<Loading />} file={`${pdfPath}`}>
            <PdfPage loading={<Loading />} pageNumber={props.pageNumber + 1} />
          </Document>
        </div>
      );
    } else {
      results = <Loading />;
    }
  } else if (props.viewType === "external") {
    results = (
      <div className="independentReadingDiv">
        <div className="independentReadingHeader">Independent Reading</div>
        Refer to your teacher instructions for what to read. To start recording, simply press “Start.” When you’ve
        finished recording, press pause and turn in your assignment.
      </div>
    );
  } else if (props.viewType === "paperback") {
    results = (
      <div className="independentReadingDiv">
        <div className="independentReadingHeader">{props.bookName}</div>
        Refer to your teacher instructions for which sections to read. To start recording, simply press “Start.” When
        you’ve finished recording, press pause and turn in your assignment.
      </div>
    );
  } else {
    const arrayOfOwlBotText = [];

    const noNewLines = props.page?.content.split("\n");
    if (noNewLines) {
      for (let i = 0; i < noNewLines.length; i++) {
        const noSpaces = noNewLines[i]?.split(" ") || [];
        for (let z = 0; z < noSpaces.length; z++) {
          const addedString = noSpaces[z];
          arrayOfOwlBotText.push(
            <OwlBotWord
              key={`owl-bot-word-${addedString}-${i}-${z}`}
              word={addedString + " "}
              onClick={() => {
                owlBotOnClick(addedString?.split("\n")?.[0]?.replace(/\W/g, "").toLowerCase() || "");
              }}
            />,
          );
        }
        if (i === noNewLines.length - 1) {
          if (props.page?.content.endsWith("\n")) {
            arrayOfOwlBotText.push(<span key={"newLine" + i}>{"\n"}</span>);
          }
        } else {
          arrayOfOwlBotText.push(<span key={"newLine" + i}>{"\n"}</span>);
        }
      }
    }

    const speechStyles = {
      container: {
        marginRight: "10px",
      },
      text: {},
      play: {
        hover: {},
        button: {
          cursor: "pointer",
          outline: "none",
          backgroundColor: "white",
          border: "solid 3px rgb(221 221 221)",
          borderRadius: 30,
          height: "35px",
          width: "35px",
        },
      },
      pause: {
        hover: {},
        button: {},
      },
      stop: {
        hover: {},
        button: {},
      },
      resume: {
        hover: {},
        button: {},
      },
    };

    const definitionHeader = (
      <>
        <Speech styles={speechStyles} lang="en-US" rate="0.75" voice="Samantha" text={word} />
        {word}
      </>
    );

    results = (
      <div className="readerPage">
        <div
          className="pageContentDiv"
          style={{
            whiteSpace: "pre-wrap",
            cursor: "pointer",
            fontSize: props.fontSize < 12 ? 16 : props.fontSize,
            lineHeight: props.fontSize + 12 + "px",
            fontFamily: props.page?.fontFamily ? props.page.fontFamily : "Futura",
          }}
        >
          <div className="pageContent">
            <div dangerouslySetInnerHTML={{ __html: enableTranscriptView ? props.correctString || "" : "" }} />
            {enableTranscriptView && props.correctString ? "" : arrayOfOwlBotText}
          </div>
          <StandardModal
            onlyOneButton
            size={ModalSize.medium}
            open={open}
            title={definitionHeader}
            cancelClose={closeModal}
            onSubmit={closeModal}
          >
            {def}
          </StandardModal>
        </div>
      </div>
    );
  }

  return results;
};
const mapStateToProps = (state: RootState): ReadleeSettingsState => {
  return {
    ...state.readleeSettingsState,
  };
};
export default connect(mapStateToProps)(ReaderView);
